"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Compass, Anchor, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  numeral: string;
  title: string;
  category: string;
  tagline: string;
  description: string;
  highlights: string[];
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  iconName: "compass" | "anchor" | "shield";
}
const servicesData: ServiceItem[] = [{
  id: "milestone-expeditions",
  numeral: "01",
  category: "Pillar One",
  title: "Milestone Expeditions",
  tagline: "Reunions, milestone birthdays, and multi-generational voyages.",
  description: "We design comprehensive multi-generational travel itineraries structured around our Anchor & Orbit method. Shared milestone moments are anchored together, while independent afternoon orbits allow each generation their own rhythm and space.",
  highlights: ["Proprietary Anchor & Orbit pacing framework", "Private cultural immersions and local storyteller access", "Tailored balance between toddler, parent, and grandparent energy levels"],
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/milestone_expedition_villa.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/milestone_expedition_villa.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/milestone_expedition_villa.webp-wvc-sizes",
  imageAlt: "Multi-generational family enjoying a private golden hour dinner overlooking the Mediterranean coast",
  iconName: "compass"
}, {
  id: "accommodations-staterooms",
  numeral: "02",
  category: "Pillar Two",
  title: "Accommodations & Staterooms",
  tagline: "Private villa buyouts, connecting luxury suites, and chartered yachts.",
  description: "Securing exclusive residences where your entire family can gather in complete seclusion. We curate private estates with dedicated staff, contiguous family wings, or private catamarans that combine sanctuary with expansive communal space.",
  highlights: ["Vetted private estate takeovers and staffed historic villas", "Guaranteed connecting suites and multi-bedroom residential wings", "Private yacht charters with tailored galley and maritime crews"],
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/luxury_yacht_staterooms.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/luxury_yacht_staterooms.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/luxury_yacht_staterooms.png-wvc-sizes",
  imageAlt: "Private villa terrace with panoramic sea views and custom lounge seating",
  iconName: "anchor"
}, {
  id: "frictionless-administration",
  numeral: "03",
  category: "Pillar Three",
  title: "Frictionless Administration",
  tagline: "Arrival matrices, dedicated private transfers, and bespoke logistics.",
  description: "We manage the complex logistical architecture behind the scenes so no family member acts as the default organizer. From staggered flight coordination to custom dietary profiles across 20 guests, everything moves with silent precision.",
  highlights: ["Staggered arrival matrix coordination with airside tarmac greeters", "Family-wide dietary database and private chef coordination", "Discreet on-the-ground itinerary management and contingency protocols"],
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/private_aviation_transfers.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/private_aviation_transfers.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/private_aviation_transfers.png-wvc-sizes",
  imageAlt: "Chauffeured private luxury transport ready for family arrival on a scenic mountain drive",
  iconName: "shield"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.65,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: ServiceItem["iconName"]) => {
    switch (name) {
      case "compass":
        return <Compass className="size-5 stroke-[1.25] text-primary" />;
      case "anchor":
        return <Anchor className="size-5 stroke-[1.25] text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 stroke-[1.25] text-primary" />;
      default:
        return <Compass className="size-5 stroke-[1.25] text-primary" />;
    }
  };
  return <section id="services-grid" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Decorative architectural hairline watermark */}
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full border border-border/40 opacity-40" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-20 bottom-12 h-80 w-80 rounded-full border border-border/30 opacity-30" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-border/80 bg-muted/60 mb-4">
            <span className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground font-sans">
              High-Touch Architectural Curation
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.2] mb-5">
            Every detail designed around how your family gathers
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground font-normal leading-relaxed">
            Three structured curation services built to eliminate logistical friction, balance individual freedom, and preserve generational harmony.
          </p>
        </div>

        {/* 3-Column Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {servicesData.map((service, index) => <motion.div key={service.id} data-index={index} variants={cardVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4
        }} className="h-full flex flex-col group">
              <Card className="h-full flex flex-col justify-between rounded-sm border border-border/70 bg-card p-0 shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-300">
                <div>
                  {/* Visual Header Image */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted border-b border-border/50">
                    <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[15%] group-hover:scale-105 transition-transform duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="px-2.5 py-1 text-xs font-semibold tracking-widest text-primary-foreground bg-primary rounded-xs">
                        {service.numeral}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-3 px-6 sm:px-7 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                        {service.category}
                      </span>
                      <div className="p-2 rounded-sm bg-accent/60 border border-border/40">
                        {renderIcon(service.iconName)}
                      </div>
                    </div>
                    <CardTitle className="font-serif text-2xl font-semibold text-card-foreground leading-snug">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-primary font-sans leading-relaxed">
                      {service.tagline}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-6 sm:px-7 space-y-5 pt-1">
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>

                    <div className="pt-2 border-t border-border/50">
                      <h4 className="text-xs uppercase tracking-wider font-semibold text-card-foreground mb-3">
                        Curated Deliverables
                      </h4>
                      <ul className="space-y-2 text-xs text-muted-foreground">
                        {service.highlights.map((highlight, hIndex) => <li key={hIndex} data-index={hIndex} className="flex items-start gap-2 leading-relaxed">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/80 mt-1.5 shrink-0" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="px-6 sm:px-7 pb-6 pt-4 border-t border-border/40 mt-6 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Custom architecture
                  </span>
                  <Button variant="ghost" size="sm" asChild className="p-0 h-auto font-sans text-xs font-semibold text-primary hover:text-primary/80 hover:bg-transparent inline-flex items-center gap-1 group/link">
                    <Link to="/AboutDiscoveryConsultationPage">
                      <span>Reserve Consultation</span>
                      <ArrowUpRight className="size-3.5 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Editorial Callout Strip */}
        <div className="mt-14 lg:mt-20 p-6 sm:p-8 rounded-sm bg-muted/70 border border-border/80 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="font-serif text-lg sm:text-xl font-medium text-foreground">
              Planning a complex milestone trip with multiple generations?
            </h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground">
              Explore how we mediate schedules, budgets, and private accommodations across entire families.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xs px-6 py-2 text-xs uppercase tracking-wider font-semibold shadow-xs">
              <Link to="/AboutDiscoveryConsultationPage">
                Request Family Discovery Call
              </Link>
            </Button>
            <Button variant="outline" asChild className="border-border text-foreground hover:bg-accent rounded-xs px-5 py-2 text-xs uppercase tracking-wider font-medium">
              <Link to="/AnchorOrbitMethodPage">
                The Method
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}